/**
 * 실행계획 표(`| Id | Operation | Name | … |`) 칸 맞추기 — 화면 폭 기준(넓은 글자 2칸)으로 다시 채운다(2026-10-11).
 *
 * 사례로 옮겨 온 계획은 이미지 판독·복사 과정에서 줄마다 칸 너비가 달라져(`| RK01N006 | 1 | 1 | 36 |` 처럼) 세로선이
 * 맞지 않는 일이 잦다. 한글 이름이 섞이면 글자 수와 화면 폭이 달라 더 어긋난다. 여기서 칸마다 가장 넓은 값에 맞춰 다시 채우고,
 * 그리는 쪽은 monoWideText(넓은 글자 = 2ch 상자)로 그린다 — 둘이 함께여야 세로선이 맞는다.
 *
 * 지키는 것: 표가 아닌 줄은 그대로 · Operation 칸의 **앞 들여쓰기**(계층)는 그대로 · 칸 수가 다른 줄은 손대지 않는다(추측하지 않는다).
 * 숫자 칸(Starts·Rows·Buffers 등)은 오른쪽 정렬. 저장값은 바꾸지 않는다 — 보여 줄 때만 쓴다.
 */
import { isWide } from './monoWide';

export function displayWidth(s) {
  let w = 0;
  for (const ch of String(s)) w += isWide(ch) ? 2 : 1;
  return w;
}

const isRow = (l) => /^\s*\|.*\|\s*$/.test(l);
// 줄임표 줄(`...` · `…`) — 긴 계획을 줄여 옮길 때 표 **가운데** 들어간다. 표를 끊는 줄이 아니다
const isEllipsis = (l) => /^\s*\|?\s*(\.{2,}|…+)\s*\|?\s*$/.test(l);
const isRule = (l) => /^\s*\|?[-+=|\s]+\|?\s*$/.test(l) && /-{3,}/.test(l);
// 숫자 칸 — 13, 2827K, 00:00:00.01, * 1, *13, 빈 칸
const NUMERIC = /^[*\s]*[\d.,:]+\s*[KMGT%]?$/;

function cellsOf(line) {
  const t = line.trim();
  return t.slice(1, -1).split('|').map((raw) => {
    // 앞 공백 하나는 구분용 — 그 뒤 공백은 들여쓰기(계층)라 남긴다
    const body = raw.startsWith(' ') ? raw.slice(1) : raw;
    return body.replace(/\s+$/, '');
  });
}

function alignBlock(lines) {
  const rows = lines.map(l => (isRow(l) && !isRule(l) ? cellsOf(l) : null));
  const counts = rows.filter(Boolean).map(r => r.length);
  if (!counts.length) return lines;
  // 가장 흔한 칸 수 — 다른 줄(판독이 칸을 합친 줄 등)은 원문 그대로
  const freq = new Map();
  for (const n of counts) freq.set(n, (freq.get(n) || 0) + 1);
  const n = [...freq.entries()].sort((a, b) => b[1] - a[1])[0][0];
  const good = rows.map(r => (r && r.length === n ? r : null));
  const width = new Array(n).fill(0);
  const numeric = new Array(n).fill(true);
  good.forEach((r, i) => {
    if (!r) return;
    r.forEach((c, j) => {
      width[j] = Math.max(width[j], displayWidth(c));
      // 첫 표 줄은 머리글일 수 있다 — 숫자 칸 판정에서 뺀다
      if (i !== good.findIndex(Boolean) && c.trim() && !NUMERIC.test(c.trim())) numeric[j] = false;
    });
  });
  const total = width.reduce((s, w) => s + w + 3, 1);
  return lines.map((l, i) => {
    const r = good[i];
    if (r) {
      return `|${r.map((c, j) => {
        const pad = ' '.repeat(width[j] - displayWidth(c));
        return ` ${numeric[j] ? pad + c : c + pad} `;
      }).join('|')}|`;
    }
    if (isRule(l)) return '-'.repeat(total);
    return l;
  });
}

// 바깥 세로선 없는 표(`Id | Operation | Name | …`) — 판독본에 흔하다(신고 2026-10-15: 바깥 선이 없어 아예 맞추지 않았다).
// SQL 의 `a || b` 와 헷갈리지 않게 **머리글(Id | … Operation)로 시작한 덩어리**만 표로 보고, `||` 가 있는 줄은 표가 아니다.
const isBareHead = (l) => /^\s*Id\s*\|.*Operation/i.test(l) && !l.includes('||');
const isBareRow = (l) => !/^\s*\|/.test(l) && !l.includes('||') && (l.match(/\|/g) || []).length >= 2;
// 바깥 선을 붙여 일반 표로 맞춘다 — 붙인 첫 공백은 구분용이라 들여쓰기(계층)에 섞이지 않는다
const wrapBare = (l) => `| ${l.replace(/\s+$/, '')} |`;

/** 글 전체에서 연속된 표 덩어리마다 칸을 맞춘다 */
export function alignPipeTables(text) {
  const lines = String(text ?? '').split('\n');
  const out = [];
  let block = [];
  let bare = false;
  const flush = () => { if (block.length) { out.push(...alignBlock(block)); block = []; } bare = false; };
  for (const l of lines) {
    if (bare) {
      if (isBareRow(l)) { block.push(wrapBare(l)); continue; }
      if (isEllipsis(l) || isRule(l)) { block.push(l); continue; }
      flush();
    }
    if (isBareHead(l)) { flush(); bare = true; block.push(wrapBare(l)); continue; }
    // 줄임표로 나뉜 위아래는 **한 표**다 — 따로 맞추면 머리글과 아래 줄의 칸 너비가 달라 세로선이 어긋난다(신고 2026-10-13:
    // 초안이 `| 0 | SELECT STATEMENT |` 뒤에 `...` 을 두고 27~31번 줄을 이었는데 머리글은 위 덩어리 너비로만 맞춰졌다)
    if (isRow(l) || (block.length && (isRule(l) || isEllipsis(l)))) block.push(l);
    else if (isRule(l)) { flush(); block.push(l); }
    else { flush(); out.push(l); }
  }
  flush();
  return out.join('\n');
}
